import type { ReactNode } from 'react';
import type { IconName } from '@launchpad-ui/icons';

export type OperatorDisplayMap = {
  '=': 'is';
  '>': 'is greater than';
  '>=': 'is greater than or equal to';
  '<': 'is less than';
  '<=': 'is less than or equal to';
  '!=': 'is not';
  '^=': 'starts with';
  '=^': 'ends with';
  '**': 'contains';
  '!**': 'does not contain';
  '//': 'matches';
  '!//': 'does not match';
};

export type Operator = keyof OperatorDisplayMap;

// The tokenized representation of the filters, e.g. FilterConfig, is an array of AttributeFilter or RawSearchFilter.
// Multi-value selection is represented as an array `value`; the optional `values`
// array is retained for callers that prefer to pass a list.
export type FilterToken = {
  field: string;
  operator: Operator;
  value?: string | number | boolean | Array<string | number | boolean>;
  values?: Array<string | number | boolean>;
};

/**
 * Sentinel value stored in a filter token to indicate "all possible values match".
 * When a token's value equals this constant, parsers should interpret the filter
 * as matching any value for the field rather than a specific one.
 *
 * Token parsers can import this constant and compare against it:
 * `token.value === PRESENT_VALUE_SENTINEL`
 */
export const PRESENT_VALUE_SENTINEL = '__present__';

/**
 * One entry in the Relational Fields option list shown in the LogicParen type cell
 * autocomplete (and in the FreeTextToken's "Relational Fields" Group section).
 * `value` is the literal prefix written into the query (e.g. `anySpan(...)`);
 * `displayName` is the user-facing label (defaults to `value` when omitted).
 */
export interface RelationalFieldOption {
  value: string;
  displayName?: string;
  /** Optional helper text rendered as a description under the option's label. */
  description?: string;
  /** Opaque scope that fields inside this group belong to; matched against FieldOptions.scope. */
  scope?: string;
  /**
   * When `false`, the option is withheld from the add-menus (FreeText Group section and the
   * LogicParen type cell) so no new group of this type can be created, but it still classifies
   * already-hydrated groups for field scoping. Defaults to creatable (`true`).
   */
  creatable?: boolean;
}

export interface FieldValueSelectorProps {
  field: FieldOptions;
  values: Array<string | number | boolean>;
  onChange: (values: Array<string | number | boolean>) => void;
  onClose: () => void;
  immediate: boolean;
}

export interface InlineLabelToken {
  operator: Operator;
  values: Array<string | number | boolean>;
  fieldOption: FieldOptions;
}

export type RenderInlineLabel = (token: InlineLabelToken) => ReactNode;

/** Which autocomplete list an option was offered from. */
export type AutocompletePart = 'key' | 'logic' | 'op' | 'relational' | 'value';

/** The highlighted autocomplete option, described for a consumer's details renderer. */
export interface AutocompleteOptionDetails {
  part: AutocompletePart;
  /** Raw option value: a field key, logical/operator token, relational prefix, or value. */
  value: string;
  /** Label shown for the option when the catalog supplied one. */
  displayName?: string;
  /** Helper text supplied by the option catalog. */
  description?: string;
  /** Field the option belongs to — the option itself when `part` is `key`. */
  field?: string;
  /** Catalog entry for `field`, when the bar has one. */
  fieldOption?: FieldOptions;
  /** Relational group definition, for a `relational` option. */
  relationalFieldOption?: RelationalFieldOption;
  /** Approximate result count the catalog reported for a value option. */
  resultsCount?: number;
  /** Operator on the token being edited, for value options. */
  operator?: Operator;
}

/**
 * Renders the metadata panel beside the autocomplete list for the highlighted option.
 * Return `null` for options the consumer has nothing to say about. The panel is omitted when
 * nothing in the open list is describable; otherwise a skipped row keeps the last details visible.
 */
export type RenderAutocompleteDetails = (option: AutocompleteOptionDetails) => ReactNode;

/** Sample value on a field; `approxCount` is optional when catalogs omit it. */
export type FieldSampleValue = { value: string; approxCount?: number; displayValue?: string };

export type FieldOptions = {
  field: string;
  /** Display label shown in the key autocomplete instead of the raw field name. */
  displayValue?: string;
  /** Opaque scope tag; a field is only offered/looked up where the enclosing group's scope matches. */
  scope?: string;
  /**
   * Semantic value type. `timestamp` keeps numeric comparison operators while accepting
   * the human-readable date/time strings users copy from observability tables.
   */
  valueType?: 'string' | 'number' | 'boolean' | 'duration' | 'timestamp';
  supportsMultipleValues?: boolean;
  /**
   * When true, the value cell renders as a multiselect autocomplete. The input displays the
   * current comma-separated values; selecting or deselecting autocomplete options adds or
   * removes individual values from that string.
   */
  multi?: boolean;
  /**
   * Explicit list of operators to show for this field.
   * When provided, overrides the default operator list derived from valueType/supportsMultipleValues.
   */
  allowedOperators?: Operator[];
  /**
   * When `true`, lozenge cells (key and value) for this field render as
   * editable inputs that accept arbitrary user-typed text. When anything else
   * (`false` or `undefined`), the cells render as read-only buttons that only
   * commit values selected from the autocomplete popover, and the cell labels
   * use the matching `displayValue` (field-level or per-sample) instead of the
   * raw value.
   */
  allowCustom?: boolean;
  /**
   * When true, selecting this field from the FreeText key autocomplete creates the
   * token in the "exists" state (value = PRESENT_VALUE_SENTINEL) instead of with an
   * empty value. The token reads "<field> exists" and matches any value until the
   * user narrows it to specific ones from the value dropdown.
   */
  defaultToPresent?: boolean;
  /** When set, the value dropdown offers an action with this label that clears the field's values. */
  clearValuesLabel?: string;
  sampleValues: FieldSampleValue[];
  renderValueSelector?: (props: FieldValueSelectorProps) => ReactNode;
  renderTriggerLabel?: RenderInlineLabel;
  iconName?: IconName;
  description?: string;
  /** Whether this field's values are still being fetched, so the picker can say so. */
  isLoadingValues?: boolean;
  /**
   * Optional grouping label. When set, the field autocomplete renders the option under a section
   * with this label; when any option sets it, sections are ordered alphabetically. Consumers that
   * don't set it keep the default flat "Fields" section.
   */
  fieldCategory?: string;
};

/**
 * A token produced by the Lezer parser.
 * .text is the exact original substring; .location offsets are used for all
 * raw-string mutations — never store a modified token tree.
 */
export type ParsedToken =
  | {
      type: 'FILTER';
      field: string;
      operator: Operator;
      value: string | number | boolean | Array<string | number | boolean>;
      /** Unquoted value segments as typed. Same as `value` for parsed tokens; optional on synthetic ones. */
      rawValues?: string[];
      negated?: boolean;
      text: string;
      location: { start: { offset: number }; end: { offset: number } };
    }
  | {
      type: 'FREE_TEXT';
      value: string;
      text: string;
      location: { start: { offset: number }; end: { offset: number } };
    }
  | {
      type: 'LOGIC_BOOL';
      value: 'AND' | 'OR';
      text: string;
      location: { start: { offset: number }; end: { offset: number } };
    }
  | {
      /** Unary negation applied to the following filter or expression. */
      type: 'LOGIC_NOT';
      value: 'NOT';
      text: string;
      location: { start: { offset: number }; end: { offset: number } };
    }
  | {
      type: 'EXPRESSION';
      /** Parsed children of the parenthetical group. */
      inner: ParsedToken[];
      /**
       * Optional identifier glued to the opening paren in the source query
       * (e.g. `anySpan(...)` parses to `expressionType: 'anySpan'`). Undefined
       * for plain `(...)` expressions.
       */
      expressionType?: string;
      text: string;
      location: { start: { offset: number }; end: { offset: number } };
    };

export type ExpressionToken = Extract<ParsedToken, { type: 'EXPRESSION' }>;

/**
 * Synthetic token produced by FilterBar's flattenTokens — not emitted by the parser.
 * Represents either the opening or closing parenthesis of an EXPRESSION group.
 */
export type PARENToken = {
  type: 'PAREN';
  side: 'open' | 'close';
  /** The original EXPRESSION token this paren belongs to, used for dispatch actions. */
  expression: ExpressionToken;
};

/**
 * The token type seen by FilterBar's render prop.
 * EXPRESSION tokens are replaced by PAREN pairs; all other parser tokens pass through.
 */
export type FlattenedToken = Exclude<ParsedToken, { type: 'EXPRESSION' }> | PARENToken;

/**
 * Directs focus to a specific token and optional sub-cell after a state mutation.
 * 'end' targets the trailing input.
 */
export type FocusOverride = {
  itemKey: string;
  part?: 'key' | 'op' | 'value';
  caret?: 'start' | 'end';
  activate?: boolean;
};

export interface QueryBuilderChangeEvent {
  /** The committed raw query string. */
  query: string;
  /** Parsed tokens derived from the committed query string. */
  tokens: ParsedToken[];
}

export type QueryBuilderState = {
  /** Raw query string — updated on every keystroke */
  query: string;
  /** Lags query — only updated on blur/enter/explicit commit */
  committedQuery: string;
  /** Directs focus after a state mutation; consumed by useApplyFocusOverride */
  focusOverride: FocusOverride | null;
};

export type QueryBuilderAction =
  | { type: 'UPDATE_FILTER_KEY'; token: ParsedToken; key: string; newOp?: Operator; focusOverride?: FocusOverride }
  | {
      type: 'UPDATE_FILTER_OP';
      token: ParsedToken;
      op: Operator;
      /** When set, replaces the token value in the same mutation (used to apply or leave exists). */
      value?: string | Array<string | number | boolean>;
      focusOverride?: FocusOverride;
    }
  | {
      type: 'UPDATE_FILTER_VALUE';
      token: ParsedToken;
      value: string | Array<string | number | boolean>;
      focusOverride?: FocusOverride;
    }
  | { type: 'DELETE_TOKEN'; token: ParsedToken; focusOverride?: FocusOverride }
  | { type: 'APPEND_TEXT'; text: string; focusOverride?: FocusOverride }
  | { type: 'SET_QUERY'; query: string; focusOverride?: FocusOverride }
  /** `force` fires onChange even when the query is unchanged since the last
   *  commit — e.g. Enter re-runs the search on the already-committed query. */
  | { type: 'COMMIT_QUERY'; force?: boolean }
  | { type: 'RESET_FOCUS_OVERRIDE' }
  /** Restores a previous query snapshot from the undo stack. Sets both query and
   *  committedQuery so the restored state is immediately committed. */
  | { type: 'UPDATE_QUERY'; query: string; focusOverride?: FocusOverride }
  /** Replaces the contiguous span covered by the given tokens with a replacement string. */
  | { type: 'REPLACE_SELECTION'; tokens: ParsedToken[]; replacement: string; focusOverride?: FocusOverride }
  /** Wraps the contiguous span covered by the given tokens in parentheses. */
  | { type: 'WRAP_SELECTION'; tokens: ParsedToken[] }
  /** Removes the parentheses of an expression group, keeping its inner tokens intact. */
  | { type: 'UNWRAP_EXPRESSION'; token: ExpressionToken; focusOverride?: FocusOverride }
  /** Updates the optional type prefix glued to an EXPRESSION's opening paren (or removes
   *  it when the new value is empty/undefined). */
  | {
      type: 'UPDATE_EXPRESSION_TYPE';
      token: ExpressionToken;
      expressionType: string | undefined;
      focusOverride?: FocusOverride;
    };
